import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getUserData } from "../services/authService";
import { uploadBook, getAllBooks } from "../services/bookService";

function Admin() {
  const { currentUser, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [books, setBooks] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState("fr");

  console.log("🎯 Admin component render - currentUser:", currentUser, "authLoading:", authLoading);

  const booksData = [
    { id: 1, titleFr: "Management de Projet Intégral", titleEn: "Integral Project Management", categoryFr: "Management de projet", categoryEn: "Project Management", icon: "📊" },
    { id: 2, titleFr: "L'État d'Esprit du Leader", titleEn: "The Leader's Mindset", categoryFr: "Leadership", categoryEn: "Leadership", icon: "👤" },
    { id: 3, titleFr: "L'Humain au Cœur du Projet", titleEn: "Humans at the Heart of the Project", categoryFr: "Management de projet", categoryEn: "Project Management", icon: "❤️" },
    { id: 4, titleFr: "Le Modèle Symphonique", titleEn: "The Symphonic Model", categoryFr: "Management de projet", categoryEn: "Project Management", icon: "🎵" },
    { id: 5, titleFr: "Planification Harmonieuse", titleEn: "Harmonious Planning", categoryFr: "Management de projet", categoryEn: "Project Management", icon: "📅" },
    { id: 6, titleFr: "Gestion des Risques en Musique", titleEn: "Risk Management in Music", categoryFr: "Management de projet", categoryEn: "Project Management", icon: "⚠️" },
    { id: 7, titleFr: "24 Principes pour Transformer sa Vie", titleEn: "24 Principles to Transform Your Life", categoryFr: "Développement personnel", categoryEn: "Personal Development", icon: "✨" },
    { id: 8, titleFr: "Le Pouvoir de la Musique", titleEn: "The Power of Music", categoryFr: "Développement personnel", categoryEn: "Personal Development", icon: "🎧" }
  ];

  useEffect(() => {
    let isMounted = true;
    let hasNavigated = false; // ✅ Empêche les navigations multiples

    const init = async () => {
      try {
        console.log("🔍 Admin init - currentUser:", currentUser, "authLoading:", authLoading);

        // ✅ ATTENDRE que l'auth soit prête
        if (authLoading) {
          console.log("⏳ Auth en cours de chargement, attente...");
          return;
        }

        if (!currentUser) {
          console.log("❌ Pas de currentUser après chargement auth");
          if (isMounted && !hasNavigated) {
            hasNavigated = true;
            navigate("/login");
          }
          return;
        }

        console.log("2️⃣ Récupération userData...");
        const userResult = await getUserData(currentUser.uid);
        console.log("📊 userResult:", userResult);

        if (!isMounted) return;

        if (!userResult.success || !userResult.data) {
          console.error("❌ Erreur getUserData");
          setError("Erreur de chargement des données utilisateur");
          setLoading(false);
          return;
        }

        console.log("👤 Role:", userResult.data.role);

        if (userResult.data.role !== "admin") {
          console.log("⛔ Pas admin");
          if (!hasNavigated) {
            hasNavigated = true;
            alert("Accès refusé - Réservé aux administrateurs");
            navigate("/dashboard");
          }
          return;
        }

        console.log("3️⃣ Chargement livres...");
        const booksResult = await getAllBooks();
        console.log("📚 booksResult:", booksResult);

        if (!isMounted) return;

        if (booksResult.success) {
          setBooks(booksResult.books || []);
        } else {
          setBooks([]);
        }

        setLoading(false);
        console.log("✅ Init terminée");

      } catch (error) {
        console.error("💥 ERREUR:", error);
        if (isMounted) {
          setError(error.message);
          setLoading(false);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [currentUser, navigate, authLoading]); // ✅ Ajout de authLoading

  const handleFileUpload = async (bookIndex, file) => {
    if (!file || file.type !== "application/pdf") {
      alert("Veuillez sélectionner un PDF");
      return;
    }

    const bookInfo = booksData[bookIndex];
    setUploadProgress((prev) => ({ ...prev, [bookIndex]: 0 }));

    try {
      const result = await uploadBook(
        file,
        {
          title: selectedLanguage === 'fr' ? bookInfo.titleFr : bookInfo.titleEn,
          author: "Dr. Arthur MOMO KOUNTCHOU",
          category: selectedLanguage === 'fr' ? bookInfo.categoryFr : bookInfo.categoryEn,
          icon: bookInfo.icon,
          bookNumber: bookInfo.id,
          language: selectedLanguage,
        },
        (progress) => setUploadProgress((prev) => ({ ...prev, [bookIndex]: progress }))
      );

      if (result.success) {
        alert(`Livre ${bookInfo.id} uploadé avec succès !`);
        const booksResult = await getAllBooks();
        if (booksResult.success) {
          setBooks(booksResult.books || []);
        }
      } else {
        alert(`Erreur: ${result.error}`);
      }
    } catch (error) {
      alert(`Erreur: ${error.message}`);
    }

    setUploadProgress((prev) => {
      const newProgress = { ...prev };
      delete newProgress[bookIndex];
      return newProgress;
    });
  };

  const isBookUploaded = (bookNumber, language) => {
    return books.some((book) => book.bookNumber === bookNumber && book.language === language);
  };

  const booksInSelectedLanguage = books.filter(book => book.language === selectedLanguage);

  // ✅ Afficher loading pendant que l'auth charge
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">⏳</div>
          <div className="text-xl text-gray-600">Chargement de l'authentification...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-red-50 border border-red-300 rounded-lg p-6">
          <h2 className="text-xl font-bold text-red-900 mb-4">❌ Erreur</h2>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => navigate("/dashboard")}
            className="w-full bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Retour au tableau de bord
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin text-6xl mb-4">⏳</div>
          <div className="text-xl text-gray-600">Vérification des accès...</div>
          <div className="text-sm text-gray-500 mt-2">
            Ouvrez la console (F12) pour voir les détails
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-6xl mx-auto px-4 space-y-8">
        <div className="flex justify-between items-center mb-6">
          <button
            onClick={() => navigate("/dashboard")}
            className="text-blue-600 hover:text-blue-800 font-semibold"
          >
            ← Retour au tableau de bord
          </button>
          <div className="text-sm text-gray-600">Connecté en tant qu'administrateur</div>
        </div>

        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6 rounded-lg shadow">
          <h1 className="text-3xl font-bold mb-2">🔐 Panneau d'administration</h1>
          <p>Téléchargez les livres de la bibliothèque</p>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">
            🌐 Langue des livres à uploader
          </h2>
          <div className="flex gap-4">
            <button
              onClick={() => setSelectedLanguage('fr')}
              className={`px-6 py-3 rounded-lg font-semibold transition ${
                selectedLanguage === 'fr'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🇫🇷 Français
            </button>
            <button
              onClick={() => setSelectedLanguage('en')}
              className={`px-6 py-3 rounded-lg font-semibold transition ${
                selectedLanguage === 'en'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              🇬🇧 English
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-4xl font-bold text-blue-600">
              {booksInSelectedLanguage.length} / 8
            </div>
            <div className="text-gray-600 mt-2">
              Livres {selectedLanguage === 'fr' ? 'français' : 'anglais'}
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-4xl font-bold text-green-600">
              {books.filter(b => b.language === 'fr').length} / 8
            </div>
            <div className="text-gray-600 mt-2">Livres français</div>
          </div>
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <div className="text-4xl font-bold text-orange-600">
              {books.filter(b => b.language === 'en').length} / 8
            </div>
            <div className="text-gray-600 mt-2">Livres anglais</div>
          </div>
        </div>

        <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4">
          <p className="text-yellow-800 font-semibold">
            ⚠️ Assurez-vous d'uploader les bons PDFs selon la langue sélectionnée !
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {booksData.map((book, index) => {
            const uploaded = isBookUploaded(book.id, selectedLanguage);
            const progress = uploadProgress[index];
            const title = selectedLanguage === 'fr' ? book.titleFr : book.titleEn;
            const category = selectedLanguage === 'fr' ? book.categoryFr : book.categoryEn;
            
            return (
              <div
                key={book.id}
                className={`p-4 rounded-lg border ${
                  uploaded ? "border-green-300 bg-green-50" : "border-gray-200 bg-white"
                } shadow-sm`}
              >
                <div className="flex items-start gap-4">
                  <div className="text-center">
                    <div className="text-3xl">{book.icon}</div>
                    <div className="text-xs text-gray-600 mt-1">Livre #{book.id}</div>
                    <div className="text-xs font-semibold mt-1">
                      {selectedLanguage === 'fr' ? '🇫🇷 FR' : '🇬🇧 EN'}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900 mb-1">{title}</h3>
                    <p className="text-sm text-gray-600 mb-2">Dr. Arthur MOMO KOUNTCHOU</p>
                    <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded">
                      {category}
                    </span>
                  </div>
                  <div className="text-right">
                    {uploaded ? (
                      <div className="text-green-600 font-semibold">✓ Uploadé</div>
                    ) : progress !== undefined ? (
                      <div className="w-32">
                        <div className="text-sm text-gray-600 mb-1">{Math.round(progress)}%</div>
                        <div className="w-full bg-gray-200 rounded-full h-2">
                          <div
                            className="bg-blue-600 h-2 rounded-full transition-all"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                      </div>
                    ) : (
                      <label className="cursor-pointer inline-block bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700">
                        📤 Choisir PDF
                        <input
                          type="file"
                          accept="application/pdf"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleFileUpload(index, file);
                          }}
                        />
                      </label>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default Admin;
